import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { AlbumsIcon, ChatIcon, CloseIcon, PhotosIcon, PlusIcon } from '../components/Icons';
import { BlockingProgress } from '../components/BlockingProgress';
import {
  api,
  thumbUrl,
  type DropSetup,
  type FamilyDrop,
} from '../lib/api';

export function DropsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [drops, setDrops] = useState<FamilyDrop[]>([]);
  const [setup, setSetup] = useState<DropSetup | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [audienceId, setAudienceId] = useState('');
  const [conversationIds, setConversationIds] = useState<string[]>([]);
  const [startsOn, setStartsOn] = useState('');
  const [endsOn, setEndsOn] = useState('');

  useEffect(() => {
    let current = true;
    void Promise.all([api.drops(), api.dropSetup()])
      .then(([dropResult, setupResult]) => {
        if (!current) return;
        setDrops(dropResult.drops);
        setSetup(setupResult);
        setError(null);
      })
      .catch((caught) => {
        if (current) setError(caught instanceof Error ? caught.message : 'Could not load Family Drops.');
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => { current = false; };
  }, []);

  const selectedAudience = useMemo(
    () => setup?.audiences.find((audience) => audience.id === audienceId) ?? null,
    [setup, audienceId],
  );
  const selectedPeople = setup?.people.filter((person) => (
    conversationIds.includes(person.userId) || person.userId === user?.id
  )) ?? [];

  const chooseAudience = (nextAudienceId: string, resetConversation = false) => {
    setAudienceId(nextAudienceId);
    const audience = setup?.audiences.find((candidate) => candidate.id === nextAudienceId);
    if (audience && (resetConversation || conversationIds.length === 0)) {
      setConversationIds(audience.memberIds.filter((id) => id !== user?.id));
    } else if (audience) {
      setConversationIds((current) => current.filter((id) => audience.memberIds.includes(id)));
    }
  };

  const beginCreate = () => {
    const audience = setup?.audiences[0];
    setTitle('');
    setStartsOn('');
    setEndsOn('');
    setCreating(true);
    if (audience) chooseAudience(audience.id, true);
  };

  const closeCreate = () => {
    if (saving) return;
    setCreating(false);
    setTitle('');
  };

  const togglePerson = (userId: string) => {
    if (userId === user?.id) return;
    setConversationIds((current) => current.includes(userId)
      ? current.filter((id) => id !== userId)
      : [...current, userId]);
  };

  const create = async () => {
    if (!title.trim() || !audienceId || saving) return;
    setSaving(true);
    setError(null);
    try {
      const result = await api.createDrop({
        title: title.trim(),
        conversationUserIds: conversationIds,
        photoAudienceId: audienceId,
        startsAt: dateValue(startsOn, false),
        endsAt: dateValue(endsOn, true),
      });
      navigate(`/drops/${result.drop.id}`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not start this Family Drop.');
      setSaving(false);
    }
  };

  return (
    <>
      <header className="app-header drops-header">
        <div>
          <p className="eyebrow">Plans become memories</p>
          <h1>Family Drops</h1>
        </div>
        <button type="button" className="btn btn-primary btn-sm" onClick={beginCreate} disabled={!setup || saving}>
          <PlusIcon />
          New Drop
        </button>
      </header>

      <div className="page drops-page">
        {error && <div className="banner error" role="alert"><p>{error}</p></div>}

        {loading ? (
          <div className="center-fill"><div className="spinner" role="status" aria-label="Loading Family Drops" /></div>
        ) : drops.length === 0 ? (
          <section className="drops-empty">
            <div className="drops-empty-art" aria-hidden="true">
              <span /><span /><span />
              <div><PhotosIcon /></div>
            </div>
            <p className="eyebrow">One place for the whole event</p>
            <h2>Start with the conversation.<br />Keep every photo.</h2>
            <p>Create a Drop for a trip, birthday, reunion, or anything the family will want to remember.</p>
            <button type="button" className="btn btn-primary" onClick={beginCreate} disabled={!setup}>
              Start your first Drop
            </button>
          </section>
        ) : (
          <section className="drop-list" aria-label="Family Drops">
            <div className="drop-list-intro">
              <div>
                <p className="eyebrow">Shared spaces</p>
                <h2>Conversation now. Photos forever.</h2>
              </div>
              <span>{drops.length} {drops.length === 1 ? 'Drop' : 'Drops'}</span>
            </div>
            {drops.map((drop, index) => <DropCard key={drop.id} drop={drop} priority={index === 0} />)}
          </section>
        )}

        <Link className="legacy-albums-link" to="/albums">
          <span className="legacy-albums-icon"><AlbumsIcon /></span>
          <span><strong>Looking for a regular album?</strong><small>Open the original family albums</small></span>
          <span aria-hidden="true">›</span>
        </Link>
      </div>

      {creating && setup && (
        <div className="drop-builder-backdrop" role="presentation" onMouseDown={closeCreate}>
          <section className="drop-builder" role="dialog" aria-modal="true" aria-labelledby="drop-builder-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="drop-builder-head">
              <div><p className="eyebrow">New shared space</p><h2 id="drop-builder-title">Start a Family Drop</h2></div>
              <button type="button" onClick={closeCreate} aria-label="Close"><CloseIcon /></button>
            </div>

            <div className="drop-builder-scroll">
              <label className="drop-title-field">
                <span>What should we call it?</span>
                <input autoFocus value={title} maxLength={120} placeholder="e.g. OBX 2027" disabled={saving} onChange={(event) => setTitle(event.target.value)} />
              </label>

              <div className="drop-date-row">
                <label><span>Starts <small>Optional</small></span><input type="date" value={startsOn} disabled={saving} onChange={(event) => setStartsOn(event.target.value)} /></label>
                <label><span>Ends <small>Optional</small></span><input type="date" min={startsOn || undefined} value={endsOn} disabled={saving} onChange={(event) => setEndsOn(event.target.value)} /></label>
              </div>

              <fieldset className="drop-audience-fieldset">
                <legend><span>Who can see the photos and videos?</span><small>Choose one family circle</small></legend>
                <div className="drop-audience-options">
                  {setup.audiences.map((audience) => (
                    <label className={audience.id === audienceId ? 'selected' : ''} key={audience.id}>
                      <input type="radio" name="photo-audience" checked={audience.id === audienceId} disabled={saving} onChange={() => chooseAudience(audience.id, true)} />
                      <span className="audience-mark"><PhotosIcon /></span>
                      <span><strong>{audience.label}</strong><small>{audience.memberIds.length} people can see and comment</small></span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <fieldset className="drop-audience-fieldset conversation-audience">
                <legend><span>Who’s in the conversation?</span><small>Only these people see the private chat</small></legend>
                <div className="drop-people-grid">
                  {(setup.people.filter((person) => selectedAudience?.memberIds.includes(person.userId))).map((person) => {
                    const isYou = person.userId === user?.id;
                    const selected = isYou || conversationIds.includes(person.userId);
                    return (
                      <label className={selected ? 'selected' : ''} key={person.userId}>
                        <input type="checkbox" checked={selected} disabled={saving || isYou} onChange={() => togglePerson(person.userId)} />
                        <span className="drop-person-avatar" aria-hidden="true">{person.displayName.slice(0, 1).toUpperCase()}</span>
                        <span>{isYou ? 'You' : person.displayName}</span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>

              <div className="drop-privacy-summary">
                <div><ChatIcon /><span><strong>{selectedPeople.length} in the conversation</strong><small>Private coordination and media</small></span></div>
                <div><PhotosIcon /><span><strong>{selectedAudience?.label ?? 'Choose a photo audience'}</strong><small>Only intentionally included media appears here</small></span></div>
              </div>
            </div>

            <button type="button" className="btn btn-primary drop-create-button" disabled={!title.trim() || !audienceId || saving} onClick={() => void create()}>
              {saving ? 'Starting your Drop…' : `Start ${title.trim() || 'Family Drop'}`}
            </button>
          </section>
        </div>
      )}

      {saving && <BlockingProgress title="Starting your Family Drop…" detail="Creating the private conversation and shared media space." />}
    </>
  );
}

function DropCard({ drop, priority }: { drop: FamilyDrop; priority: boolean }) {
  return (
    <Link className="drop-card" to={`/drops/${drop.id}`}>
      <div className="drop-card-cover">
        {drop.coverFileId
          ? <img src={thumbUrl(drop.coverFileId, 900)} alt="" loading={priority ? 'eager' : 'lazy'} />
          : <div className="drop-card-placeholder" aria-hidden="true"><span /><span /><PhotosIcon /></div>}
        <span className="drop-card-photo-count">{drop.photoCount} {drop.photoCount === 1 ? 'item' : 'items'}</span>
      </div>
      <div className="drop-card-copy">
        <div>
          <p>{formatDropDates(drop)}</p>
          <h3>{drop.title}</h3>
        </div>
        <div className="drop-card-meta">
          {drop.canChat && <span><ChatIcon />{drop.participants.length}</span>}
          <span><PhotosIcon />{drop.photoAudience.label}</span>
        </div>
      </div>
    </Link>
  );
}

function dateValue(value: string, endOfDay: boolean): number | null {
  if (!value) return null;
  const suffix = endOfDay ? 'T23:59:59' : 'T00:00:00';
  const timestamp = new Date(`${value}${suffix}`).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

function formatDropDates(drop: FamilyDrop): string {
  if (!drop.startsAt && !drop.endsAt) return 'Family Drop';
  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  if (drop.startsAt && drop.endsAt) {
    return `${new Date(drop.startsAt).toLocaleDateString(undefined, options)} — ${new Date(drop.endsAt).toLocaleDateString(undefined, options)}`;
  }
  return new Date(drop.startsAt ?? drop.endsAt!).toLocaleDateString(undefined, { ...options, year: 'numeric' });
}
